import React, { useCallback, useEffect, useRef } from "react";
import {
  BaselineSeries,
  IChartApi,
  ISeriesApi,
  LineSeries,
  Time,
} from "lightweight-charts";
import { AuctionMarketEvent } from "../../lib/interface/AuctionMarketEvent.interface";

interface AuctionMarketZonesProps {
  chartInstance: IChartApi | null;
  candleSeries: ISeriesApi<"Candlestick"> | null;
  isEnabled: boolean;
  auctionMarketEvents: AuctionMarketEvent[];
  formatTime: (timestamp: number) => number;
}

/**
 * Renders Auction Market zones (VAH to VAL) as a transparent orange area
 * with a POC line overlay.
 */
export const AuctionMarketZones: React.FC<AuctionMarketZonesProps> = ({
  chartInstance,
  candleSeries,
  isEnabled,
  auctionMarketEvents,
  formatTime,
}) => {
  const zoneSeriesRefs = useRef<Array<ISeriesApi<"Baseline">>>([]);
  const pocLineRefs = useRef<Array<ISeriesApi<"Line">>>([]);

  const safeRemoveSeries = useCallback(
    (series: ISeriesApi<"Baseline"> | ISeriesApi<"Line"> | null) => {
      if (!chartInstance || !series) return;
      try {
        chartInstance.removeSeries(series);
      } catch {
        // Ignore removal errors when chart or series is already disposed
      }
    },
    [chartInstance]
  );

  const clearSeries = useCallback(() => {
    zoneSeriesRefs.current.forEach((series) => safeRemoveSeries(series));
    pocLineRefs.current.forEach((series) => safeRemoveSeries(series));
    zoneSeriesRefs.current = [];
    pocLineRefs.current = [];
  }, [safeRemoveSeries]);

  useEffect(() => {
    if (!chartInstance || !candleSeries) return;
    // Remove previous series if any
    clearSeries();

    if (!isEnabled || !auctionMarketEvents.length) return;

    const isFiniteNumber = (value: unknown): value is number =>
      typeof value === "number" && Number.isFinite(value);

    // Use all events as received from backend (no timeframe filtering)
    const events = auctionMarketEvents.filter((event) => {
      const marketPeriodStart = (event as { marketPeriodStart?: number })
        .marketPeriodStart;
      const marketPeriodEnd = (event as { marketPeriodEnd?: number })
        .marketPeriodEnd;
      return (
        isFiniteNumber(event.valueAreaHigh) &&
        isFiniteNumber(event.valueAreaLow) &&
        isFiniteNumber(event.poc) &&
        isFiniteNumber(event.timestamp) &&
        isFiniteNumber(marketPeriodStart) &&
        isFiniteNumber(marketPeriodEnd)
      );
    });
    if (!events.length) return;

    const times = events
      .map((event) => formatTime(event.timestamp))
      .filter((time) => Number.isFinite(time));
    if (!times.length) return;

    const buckets = new Map<number, AuctionMarketEvent>();
    for (const event of events) {
      const marketPeriodStart = (event as { marketPeriodStart: number })
        .marketPeriodStart;
      if (!isFiniteNumber(marketPeriodStart)) continue;
      const existing = buckets.get(marketPeriodStart);
      if (!existing || event.timestamp > existing.timestamp) {
        buckets.set(marketPeriodStart, event);
      }
    }

    const sortedBuckets = Array.from(buckets.entries()).sort(
      ([a], [b]) => a - b
    );

    for (const [bucketStart, bucketEvent] of sortedBuckets) {
      const timeStart = formatTime(bucketStart);
      const timeEndRaw = formatTime(
        (bucketEvent as { marketPeriodEnd: number }).marketPeriodEnd
      );
      if (!Number.isFinite(timeStart) || !Number.isFinite(timeEndRaw)) continue;
      const timeEnd = timeEndRaw > timeStart ? timeEndRaw : timeStart + 1;

      const vah = bucketEvent.valueAreaHigh;
      const val = bucketEvent.valueAreaLow;
      const poc = bucketEvent.poc;

      const zoneSeries = chartInstance.addSeries(BaselineSeries, {
        baseValue: { type: "price", price: val },
        topFillColor1: "rgba(255, 128, 54, 0.12)",
        topFillColor2: "rgba(255, 128, 54, 0.12)",
        topLineColor: "rgba(255, 128, 54, 0.3)",
        bottomFillColor1: "rgba(255, 128, 54, 0)",
        bottomFillColor2: "rgba(255, 128, 54, 0)",
        bottomLineColor: "rgba(255, 128, 54, 0)",
        lineWidth: 1,
        lineVisible: false,
        priceLineVisible: false,
        lastValueVisible: false,
      });
      zoneSeries.setData([
        { time: timeStart as Time, value: vah },
        { time: timeEnd as Time, value: vah },
      ]);
      zoneSeriesRefs.current.push(zoneSeries);

      const pocLine = chartInstance.addSeries(LineSeries, {
        color: "rgba(255, 140, 0, 0.3)",
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
      });
      pocLine.setData([
        { time: timeStart as Time, value: poc },
        { time: timeEnd as Time, value: poc },
      ]);
      pocLineRefs.current.push(pocLine);
    }

    return () => {
      clearSeries();
    };
  }, [
    chartInstance,
    candleSeries,
    isEnabled,
    auctionMarketEvents,
    formatTime,
    clearSeries,
  ]);

  return null;
};
